import { add, sub } from 'date-fns';
import { capitalize } from 'lodash';
import type { IntlShape } from 'react-intl';

import { getIntervalValues } from '~/components/DashboardV2/DashboardV2.utils';
import { parseDate } from '~/eds/src/utils/parseDate';
import type { useFormatDateReturnType } from '~/i18n/hooks/useFormatDate/types';
import type { DateFormat } from '~/i18n/hooks/useFormatDate/utils';
import type { useFormatNumberReturnType } from '~/i18n/hooks/useFormatNumber/types';
import { Bucket } from '~/types/DashboardV2';
import { capitalizeWords } from '~/utils/strings';

const intervalMapping: Record<string, string> = {
  M: 'MONTH',
  d: 'DAY',
  y: 'YEAR',
};
const durationMapping: Record<string, string> = {
  M: 'months',
  d: 'days',
  y: 'years',
};

const localizationMapping: Record<string, DateFormat> = {
  M: 'month',
  d: 'day',
  y: 'year',
  q: 'month',
  w: 'day',
};

export const formatTitleCase = ({
  label,
  value,
  bucketValue,
}: Bucket): Bucket => {
  return { label: capitalizeWords(label), value, bucketValue: bucketValue };
};

export const formatDate = (
  { label, value }: Bucket,
  interval: string,
  multiple: number,
  formatDateByLocale: useFormatDateReturnType,
  intl: IntlShape,
  selectedInterval?: string,
) => {
  if (multiple > 1) {
    const labelDate = parseDate(label, true);

    if (labelDate) {
      const lastDate = sub(
        add(labelDate, { [durationMapping[interval]]: multiple }),
        { days: 1 },
      );

      const startDateFormatted = formatDateByLocale(
        labelDate,
        localizationMapping[interval],
      );
      const lastDateFormatted = formatDateByLocale(
        lastDate,
        localizationMapping[interval],
      );

      const label = intl.formatMessage(
        {
          id: 'FilterDataChart.dateRange',
          defaultMessage: '{startDate} to {endDate}',
          description: 'Date range label I,E: Jan 1 to Jan 7',
        },
        {
          startDate: startDateFormatted,
          endDate: lastDateFormatted,
        },
      );

      const dateFnsObject = {
        label,
        bucketValue: `${formatDateByLocale(
          labelDate,
          'iso',
        )} to ${formatDateByLocale(lastDate, 'iso')}`,
        value,
      };

      return dateFnsObject;
    } else {
      return { label: 'null', bucketValue: 'null', value };
    }
  } else {
    const intervalForValues = selectedInterval
      ? selectedInterval
      : intervalMapping[interval];

    const parsedDate = parseDate(label, true);
    //* parsedDate should not be null but in case it is for now we will return empty array.
    const intervalValues = parsedDate
      ? getIntervalValues(parsedDate, intervalForValues)
      : [];
    const formattedDate = parsedDate
      ? formatDateByLocale(parsedDate, localizationMapping[interval])
      : null;

    return {
      label: `${formattedDate}`,
      bucketValue: intervalValues
        .map((intervalValue) => formatDateByLocale(intervalValue, 'iso'))
        .join(' to '),
      value,
    };
  }
};

export const formatBoolean = ({ label, value }: Bucket, intl: IntlShape) => {
  if (isNaN(Number(label))) {
    return { label: capitalize(label), value };
  } else {
    return {
      label:
        String(label) === '1'
          ? intl.formatMessage({
              id: 'FilterDataChart.trueValue',
              defaultMessage: 'True',
              description: 'Boolean true value label',
            })
          : intl.formatMessage({
              id: 'FilterDataChart.falseValue',
              defaultMessage: 'False',
              description: 'Boolean false value label',
            }),
      value,
    };
  }
};

export const formatNumber = (
  { label, value }: Bucket,
  interval: number,
  formattedNumber: useFormatNumberReturnType,
) => {
  const truncatedLabel = Math.trunc(Number(label));
  const firstNumber = formattedNumber(truncatedLabel, { decimal: 0 });
  if (interval > 1) {
    const lastNumber = formattedNumber(truncatedLabel + interval - 1, {
      decimal: 0,
    });

    return {
      label: `${firstNumber} - ${lastNumber}`,
      bucketValue: `${label} - ${Number(label) + interval - 1}`,
      value,
    };
  } else {
    return { label: firstNumber, bucketValue: Number(label), value };
  }
};

export const formatAge = (bucket: Bucket, intl: IntlShape, interval = 0) => {
  const numericLabel = Number(bucket.label);
  const label =
    interval === 1
      ? intl.formatMessage(
          {
            id: 'FilterDataChart.ageDays',
            defaultMessage: '{count, plural, one {# day} other {# days}}',
            description:
              'Age in days with pluralization. Example: count = 1, 2, 3',
          },
          { count: numericLabel },
        )
      : intl.formatMessage(
          {
            id: 'FilterDataChart.ageDaysRange',
            defaultMessage: '{start} - {end} days',
            description: 'Age range in days. Example: start = 1, end = 7',
          },
          {
            start: bucket.label,
            end: interval + numericLabel - 1,
          },
        );
  return {
    ...bucket,
    label,
    bucketValue: `${bucket.label} - ${interval + numericLabel - 1}`,
  };
};
